import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import { ORDER_STATUS, ORDER_STATUS_LABEL } from "../../status"

// Confirms splitting a group's not-yet-approved members off into standalone
// orders. This never approves anything — it only acts on each member's
// current status — so the copy is explicit that the split orders keep going
// through their own approval flow rather than being decided here.
//
// Backend rule: with exactly 1 approved member, the WHOLE group dissolves —
// that lone approved order is split off too, not kept — because a group of
// one doesn't mean anything. Only with ≥2 approved members does the group
// survive with just those staying.
const PartialApproveModal = ({ group, busy, onCancel, onSubmit }) => {
  const members = group?.orders || []
  const approved = members.filter((o) => o.status === ORDER_STATUS.APPROVED)
  const dissolves = approved.length === 1
  const staying = dissolves ? [] : approved
  const detaching = dissolves ? members : members.filter((o) => o.status !== ORDER_STATUS.APPROVED)
  return (
    <Dialog open onClose={() => !busy && onCancel()} fullWidth maxWidth="sm">
      <DialogTitle>
        {dissolves ? `Dissolve ${group?.orCode}?` : `Split unapproved orders out of ${group?.orCode}?`}
      </DialogTitle>
      <DialogContent>
        <MDTypography variant="body2" color="text" mb={2}>
          {dissolves
            ? "This does not approve anything. Only 1 order in this group is approved, so the whole group is dissolved — every order (including the approved one) becomes standalone and keeps going through its own approval flow."
            : "This does not approve anything. Orders already approved stay in the group; the rest are split off as standalone orders and keep going through their own approval flow."}
        </MDTypography>
        {!dissolves && (
          <>
            <MDTypography variant="caption" fontWeight="bold" color="success" textTransform="uppercase">
              Stays in {group?.orCode} ({staying.length})
            </MDTypography>
            <MDBox mb={1.5}>
              {staying.map((o) => (
                <MDTypography key={o.id} variant="body2" color="text">
                  {o.code} — {o.title}
                </MDTypography>
              ))}
            </MDBox>
          </>
        )}
        <MDTypography variant="caption" fontWeight="bold" color="warning" textTransform="uppercase">
          {dissolves ? `Becomes standalone (${detaching.length})` : `Splits off as standalone (${detaching.length})`}
        </MDTypography>
        <MDBox>
          {detaching.map((o) => (
            <MDTypography key={o.id} variant="body2" color="text">
              {o.code} — {o.title} ({ORDER_STATUS_LABEL[o.status] || o.status})
            </MDTypography>
          ))}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="dark" onClick={onCancel} disabled={busy}>
          Cancel
        </MDButton>
        <MDButton variant="gradient" color="warning" onClick={onSubmit} disabled={busy}>
          {busy ? "Splitting…" : dissolves ? "Dissolve & continue" : "Split & continue"}
        </MDButton>
      </DialogActions>
    </Dialog>
  )
}


export default PartialApproveModal
